import * as THREE from 'three';
import { getFaction } from '../factions';
import type { FactionId } from '../factions';
import { PlasmaPlume } from '../../visual';
import { buildShipSkin } from './skins/ShipSkin';
import { SHIP_BUILDERS } from './variants';
import type { EngineMount, ShipBuildResult } from './variants';

const _enhancedEngines = new WeakSet<THREE.Mesh>();
const _plumes = new WeakMap<EngineMount, PlasmaPlume>();

export function buildShipMesh(
  parent: THREE.Object3D,
  factionId: FactionId,
  primaryColor: number
): ShipBuildResult {
  const faction = getFaction(factionId);
  const skin = buildShipSkin(faction, primaryColor);
  const builder = SHIP_BUILDERS[faction.shipVariant];
  return builder(parent, skin);
}

/**
 * Replace each engine's static cone with a shader-driven PlasmaPlume and
 * keep the bright inner core additive so the throat reads as a hot reactor
 * face even at idle. Idempotent — safe to call multiple times per ship.
 */
export function enhanceEngineFlames(
  engines: readonly EngineMount[],
  plasmaColor: number,
  glowColor: number
): void {
  for (const engine of engines) {
    if (_enhancedEngines.has(engine.plume)) continue;
    _enhancedEngines.add(engine.plume);

    const parent = engine.plume.parent;
    // The static cone is replaced entirely by the plasma plume — drop it
    // from the scene so it doesn't double-up against the new shader.
    engine.plume.visible = false;

    const coreMat = engine.core.material as THREE.MeshBasicMaterial;
    coreMat.blending = THREE.AdditiveBlending;
    coreMat.depthWrite = false;
    coreMat.toneMapped = false;
    coreMat.transparent = true;

    if (!parent) continue;

    const { radius } = inferPlumeSize(engine.plume);
    // Lock the plume's length to the demo's reference ratio (3.6 / 0.46
    // = 7.83) so the silhouette matches the reference exactly. Width is
    // bumped slightly past the engine cone radius so the halo softly
    // skirts the bell rim.
    const plumeRadius = radius * 1.3;
    const plumeLength = plumeRadius * (3.6 / 0.46);
    const plume = new PlasmaPlume({
      colorHot: glowColor,
      colorCool: plasmaColor,
      radius: plumeRadius,
      length: plumeLength,
      idleLevel: 0.34
    });
    plume.object3D.position.copy(engine.local);
    parent.add(plume.object3D);
    _plumes.set(engine, plume);
  }
}

function inferPlumeSize(plume: THREE.Mesh): { radius: number; length: number } {
  // Each ship variant builds its plume cone via THREE.ConeGeometry, which
  // exposes its constructor params. Reading them lets the plasma plume
  // match the size the artist picked per variant without extra config.
  const params = (plume.geometry as THREE.ConeGeometry).parameters as
    | { radius?: number; height?: number }
    | undefined;
  return {
    radius: params?.radius ?? 0.27,
    length: params?.height ?? 1.2
  };
}

export function animateShipVisuals(
  time: number,
  thrust: number,
  engines: readonly EngineMount[],
  strobes: readonly THREE.Mesh[]
): void {
  for (const engine of engines) {
    // Inner core (bright disc inside the throat) stays lit at all times so
    // the engine reads as "hot" even at rest. Pulse blends from a slow idle
    // hum to a faster throttle pulse based on thrust.
    engine.core.visible = true;
    const idle = 0.9 + Math.sin(time * 1.6) * 0.06;
    const driven = 1.0 + Math.sin(time * 5.5) * 0.08 + thrust * 0.25;
    const corePulse = idle + (driven - idle) * Math.min(1, thrust * 4);
    engine.core.scale.setScalar(corePulse);
    const coreMat = engine.core.material as THREE.MeshBasicMaterial;
    coreMat.opacity = 0.9 + thrust * 0.1;

    const plume = _plumes.get(engine);
    if (plume) {
      plume.setThrust(thrust);
      plume.tick(time);
    }
  }

  const strobeOn = Math.floor(time * 1.7) % 2 === 0;
  for (const strobe of strobes) {
    strobe.visible = strobeOn;
  }
}
